import React, { useEffect, useRef, useState } from 'react';
import { Navbar, Nav, Container, Button, NavDropdown, Image } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaStore, FaBox, FaEnvelope, FaCog, FaSignOutAlt, FaTachometerAlt } from 'react-icons/fa';
import { FaUserCircle } from 'react-icons/fa';
import SearchBar from '../common/SearchBar';
import logoImage from '../../assets/logo.png';
import { useAuth } from '../../contexts/AuthContext';
import './Navbar.css';

const SellerNavbar = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  
  // Handle navigation with proper same-page detection
  const handleNavigation = (path, event) => {
    // Prevent navigation if already on the same page
    if (location.pathname === path) {
      console.log('Already on page, just refreshing:', path);
      event?.preventDefault();
      // Close mobile menu if it's open
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      return;
    }
    
    console.log('Navigating to:', path);
    navigate(path);
    
    // Close mobile menu after navigation
    setTimeout(() => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    }, 100);
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/seller/products?search=${encodeURIComponent(query)}`);
    }
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const displayName = (
    `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || currentUser?.email || 'User'
  );

  const avatarUrl = currentUser?.profile_picture || currentUser?.profilePicture;

  return (
    <div className="sticky-top">
    <Navbar expand="lg" className="mb-4 navbar-shadow navbar-gradient">
      <Container className="position-relative">
        {/* UniTrade Logo/Brand */}
        
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
          <Image 
            src={logoImage} 
            alt="UniTrade Logo" 
            height="25" 
            className="me-2" 
          />
          <span className="brand-text">UniTrade</span>
        </Navbar.Brand>
        
        {/* Mobile Account Icon with Username */}
        <div className="d-flex d-lg-none mobile-user-toggle" ref={toggleRef}>
          <div 
            className="nav-link d-flex align-items-center nav-user-pill" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ cursor: 'pointer' }}
          >
            {avatarUrl ? (
              <Image src={avatarUrl} alt="Profile" className="nav-user-avatar" />
            ) : (
              <FaUser className="me-1" />
            )}
            {currentUser && <span className="seller-nav-username">{displayName}</span>}
          </div>
        </div>

        <Navbar.Toggle aria-controls="basic-navbar-nav" className="d-none" />
        
        <Navbar.Collapse id="basic-navbar-nav" className={mobileMenuOpen ? "show" : ""} ref={menuRef}>
          {/* Search Bar - Desktop */}
          <div className="d-none d-lg-flex justify-content-center flex-grow-1 mx-lg-3">
            <div style={{ maxWidth: '450px', width: '100%' }}>
              <SearchBar 
                placeholder="Search products..."
                className="w-100"
                value={searchQuery}
                onChange={setSearchQuery}
                onSubmit={handleSearch}
              />
            </div>
          </div>
          
          {/* User Menu */}
          <Nav className="ms-auto">
            {/* User Dropdown - Desktop Only */}
            <div className="d-none d-lg-block">
              <NavDropdown 
                title={
                <>
                  <span className="nav-user-pill">
                    {avatarUrl ? (
                      <Image src={avatarUrl} alt="Profile" className="nav-user-avatar" />
                    ) : (
                      <FaUser className="me-2" />
                    )}
                    {currentUser && <span className="seller-nav-username">{displayName}</span>}
                  </span>
                </>
              } 
                id="seller-nav-dropdown"
                align="end"
              >
              <NavDropdown.Item as={Link} to="/profile">
                <FaUser className="me-2" /> Profile
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/seller/dashboard">
                <FaTachometerAlt className="me-2" /> Dashboard
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/seller/products">
                <FaStore className="me-2" /> My Products
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/seller/orders">
                <FaBox className="me-2" /> Orders
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/messages">
                <FaEnvelope className="me-2" /> Messages
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/settings">
                <FaCog className="me-2" /> Settings
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout}>
                <FaSignOutAlt className="me-2" /> Logout
              </NavDropdown.Item>
            </NavDropdown>
            </div>
            
            {/* Mobile Menu Items - Only visible when mobile menu is open */}
            <div className="d-lg-none w-100 mt-3">
              {mobileMenuOpen && (
                <div className="mobile-menu-items">
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/profile', e)} 
                    className={`py-2 ${location.pathname === '/profile' ? 'active disabled' : ''}`}
                  >
                    <FaUser className="me-2" /> Profile
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/seller/dashboard', e)} 
                    className={`py-2 ${location.pathname === '/seller/dashboard' ? 'active disabled' : ''}`}
                  >
                    <FaTachometerAlt className="me-2" /> Dashboard
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/seller/products', e)} 
                    className={`py-2 ${location.pathname === '/seller/products' ? 'active disabled' : ''}`}
                  >
                    <FaStore className="me-2" /> My Products
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/seller/orders', e)} 
                    className={`py-2 ${location.pathname === '/seller/orders' ? 'active disabled' : ''}`}
                  >
                    <FaBox className="me-2" /> Orders
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/messages', e)} 
                    className={`py-2 ${location.pathname === '/messages' ? 'active disabled' : ''}`}
                  >
                    <FaEnvelope className="me-2" /> Messages
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/settings', e)} 
                    className={`py-2 ${location.pathname === '/settings' ? 'active disabled' : ''}`}
                  >
                    <FaCog className="me-2" /> Settings
                  </Nav.Link>
                  <hr className="my-2" />
                  <Nav.Link onClick={handleLogout} className="py-2">
                    <FaSignOutAlt className="me-2" /> Logout
                  </Nav.Link>
                </div>
              )}
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    </div>
  );
};

export default SellerNavbar;
